import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { publicFileUrl } from "@/lib/signedFile";
import { ASSOCIATIONS } from "./data";

function PartnerChip({ name, logoUrl, ariaHidden }) {
  const nameless = !name && !!logoUrl;
  // Logo-only mode: no frame, just a big crisp logo — many partner marks
  // already include their brand name and don't need a card.
  if (nameless) {
    return (
      <div
        className="shrink-0 grid place-items-center px-8 lg:px-10"
        aria-hidden={ariaHidden ? "true" : undefined}
        aria-label="شعار شريك نجاح"
      >
        <img
          src={publicFileUrl(logoUrl)}
          alt="شعار شريك"
          className="h-32 lg:h-40 w-auto max-w-[360px] object-contain"
          loading="lazy"
        />
      </div>
    );
  }
  return (
    <div
      className="min-w-[240px] flex items-center gap-3 shrink-0 px-6 py-3"
      aria-hidden={ariaHidden ? "true" : undefined}
    >
      <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-teal-700 to-teal-900 text-white grid place-items-center font-heading font-black shrink-0 overflow-hidden">
        {logoUrl
          ? <img src={publicFileUrl(logoUrl)} alt="" className="h-full w-full object-contain p-2" />
          : (name?.[0] || "?")}
      </div>
      <div className="min-w-0">
        <div className="font-heading font-bold text-slate-900 truncate text-base">{name}</div>
        <div className="text-xs text-slate-500">شريك نجاح</div>
      </div>
    </div>
  );
}

export default function PartnersSection({ partners }) {
  const [fallback, setFallback] = useState(null);

  useEffect(() => {
    if (partners !== undefined) return; // provided by parent
    (async () => {
      try {
        const { data } = await api.get("/public/landing");
        setFallback(data.partners_list || []);
      } catch {
        setFallback([]);
      }
    })();
  }, [partners]);

  const list = (partners !== undefined ? partners : fallback) || [];
  const items = list.length > 0
    ? list.map((p) => ({ name: p.name, logo: p.logo_url }))
    : ASSOCIATIONS.map((name) => ({ name, logo: null }));

  return (
    <section className="py-10 lg:py-14 bg-cream-50 border-y border-cream-200" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-6">
        <div className="flex items-center gap-3 text-slate-700">
          <div className="h-px flex-1 bg-slate-200" />
          <p className="text-sm font-semibold tracking-wide">شركاء الأثر مع عماد</p>
          <div className="h-px flex-1 bg-slate-200" />
        </div>
      </div>
      <div className="relative overflow-hidden marquee-track" tabIndex={0} aria-label="شركاء الأثر مع عماد">
        <div className="flex gap-10 lg:gap-16 marquee w-max px-6 items-center" data-testid="partners-marquee">
          {items.map((p, i) => (
            <PartnerChip key={`p1-${i}`} name={p.name} logoUrl={p.logo} />
          ))}
          {/* Wide spacer between the two copies so viewers see only one full set on screen at a time */}
          <div className="shrink-0 w-[35vw] min-w-[240px]" aria-hidden="true" />
          {items.map((p, i) => (
            <PartnerChip key={`p2-${i}`} name={p.name} logoUrl={p.logo} ariaHidden />
          ))}
          <div className="shrink-0 w-[35vw] min-w-[240px]" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}
